/**
 * The panel every section sits in.
 *
 * One recipe, because there were six near-copies of it — `rounded-lg border
 * border-slate-800 bg-slate-900/40` in the run header, the dead-end table, the
 * contradiction table, both leaderboards and the Wiki — and they had already
 * drifted in radius and in whether the background carried an alpha.
 *
 * `bg-slate-900` is now solid rather than `/40`. The alpha existed because the page
 * was also `slate-900`, so a translucent card over it was a way to get *some*
 * separation without a second surface; now that the page is `slate-950` the card
 * has a real one, and the alpha only muddied its border.
 *
 * `Card` is the shell alone and adds no padding, so a table can be its direct child
 * and reach the card's edges. Prose and metric grids wrap in `CardBody`.
 */
import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/utils'

export function Card({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-card',
        className,
      )}
      {...props}
    />
  )
}

interface CardHeaderProps {
  /**
   * The small tracked-out label above the title.
   *
   * It names the **level of record** the card holds — `RUN`, `RANKED TARGETS`,
   * `NOT RANKED`, `DISPUTED GRAPH VALUES` — which is a real distinction in this
   * system and the one a reader most often gets wrong: a per-run ranking, the
   * cumulative board and the review ledger are three different claims. It is not a
   * decorative kicker, so do not use it for a restatement of the title.
   */
  eyebrow?: string
  title: ReactNode
  /** One or two sentences under the title. Not a tooltip — it stays on screen. */
  description?: ReactNode
  /** Controls, right-aligned against the title row. */
  actions?: ReactNode
}

export function CardHeader({
  eyebrow,
  title,
  description,
  actions,
}: CardHeaderProps) {
  return (
    <header className="flex items-start justify-between gap-4 border-b border-slate-800 px-5 py-4">
      <div className="min-w-0">
        {eyebrow && (
          <p className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
            {eyebrow}
          </p>
        )}
        <h2 className="mt-1 truncate font-display text-sm font-semibold text-slate-100">
          {title}
        </h2>
        {description && (
          <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-slate-400">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </header>
  )
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-4', className)} {...props} />
}
